import { Box, Typography, Stack, Button, Grid2 as Grid, Card } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { getChatList, markMessagesAsRead } from "../../../config/apisauce";
import useStore from "../../../states/store";
import { PAGE_PERMISSIONS } from "../../../enums/permissions/permission";
import { formatDistanceToNow } from "date-fns";
import { GenericDivider } from "../../../components/generic/GenericDivider";
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { Circle } from "@phosphor-icons/react";

const { CAN_VIEW_ALL_CHATS } = PAGE_PERMISSIONS;

export const ChatsList = (props) => {
  const {
    userId,
    setSelectedChat,
    selectedChat,
    senderDetails,
    setChatListModel,
    chatListModel
  } = props;

  const [, { hasPermission }] = useStore();
  const [filter, setFilter] = useState({ is_read: null });
  const [pagination, setPagination] = useState({ pageSize: 10, page: 1 });
  const [isLoadingChatList, setIsLoadingChatList] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleReadMessage = async (receiver, sender) => {
    await markMessagesAsRead({ receiver_id: receiver, sender_id: sender });
  };

  const handleChatClick = (chat) => {
    if (chat?.latest_message?.is_read === 0 && userId === chat?.latest_message?.receiver_id) {
      chat.latest_message.is_read = 1;
      setUnreadCount((prev) => prev - 1);
      handleReadMessage(chat?.latest_message?.receiver_id, chat?.latest_message?.sender_id);
    }
    setSelectedChat(chat);
  };

  const params = {
    user_id: userId,
    can_view_all_chats: hasPermission(CAN_VIEW_ALL_CHATS) ? 'yes' : 'no',
    page: pagination.page,
    per_page: pagination.pageSize,
    is_read: filter?.is_read
  };

  const fetchUnread = async () => {
    const { ok, data } = await getChatList({ ...params, is_read: 0 });
    if (ok) {
      setUnreadCount(data?.data?.length);
    }
  };

  const fetchChatList = async () => {
    if (!hasMore || isLoadingChatList) return;

    setIsLoadingChatList(true);
    const { ok, data } = await getChatList(params);

    if (ok) {
      if (pagination.page === 1) {
        setChatListModel(data);
      } else {
        setChatListModel(prevModel => ({
          ...data,
          data: [...(prevModel?.data || []), ...(data?.data || [])]
        }));
      }

      setHasMore(data?.data?.length === pagination.pageSize);
      setIsLoadingChatList(false);
    }
  };

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (Math.abs(scrollHeight - scrollTop - clientHeight) < 1) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const handleFilter = (value) => {
    setFilter({ is_read: value });
    setPagination({ pageSize: 10, page: 1 });
    setHasMore(true);
  };

  useEffect(() => {
    fetchChatList();
  }, [pagination.page, filter.is_read, hasPermission(CAN_VIEW_ALL_CHATS)]);

  useEffect(() => {
    fetchUnread();
  }, []);

  useEffect(() => {
    const chatChannel = window.Echo.private(`chat.${userId}`);
    chatChannel.listen("MessageSent", (messageDetails) => {
      if (messageDetails?.message) {
        if (messageDetails.receiver_id === userId && messageDetails.is_read === 0) {
          setUnreadCount(prev => prev + 1);
        }
        setChatListModel((prevModel) => {
          const updatedData = prevModel?.data?.map(chat => {
            if (chat?.id === messageDetails?.request_id) {
              return {
                ...chat,
                latest_message: {
                  ...messageDetails,
                  created_at: messageDetails.created_at
                }
              };
            }
            return chat;
          });

          return {
            ...prevModel,
            data: [
              updatedData.find(chat => chat?.id === messageDetails?.request_id),
              ...updatedData.filter(chat => chat?.id !== messageDetails?.request_id)
            ]
          };
        });
      }
    });

    return () => {
      chatChannel.stopListening("MessageSent");
    };
  }, [senderDetails?.id]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", overflowY: "auto" }}>
      <Stack direction="row" gap={1} margin={`15px 0 ${isLoadingChatList ? "8px" : "15px"} 0`} justifyContent="center">
        <Button
          variant={filter?.is_read === null ? "contained" : "outlined"}
          size="large"
          sx={{ width: "50%" }}
          onClick={() => handleFilter(null)}
        >
          All
        </Button>
        <Button
          variant={filter?.is_read === 0 ? "contained" : "outlined"}
          size="large"
          sx={{ width: "50%" }}
          onClick={() => handleFilter(0)}
        >
          Unread({unreadCount ?? 0})
        </Button>
      </Stack>
      <Box
        id="chatListBox"
        onScroll={handleScroll}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
        }}
      >
        {chatListModel?.data?.map((chat, index) => {
          const details = userId === chat?.requested_by?.id ? chat?.requested_to : chat?.requested_by;
          const latestMessage = chat?.latest_message;
          const isUnread = latestMessage?.is_read === 0 && userId === chat?.latest_message?.receiver_id;
          return (
            <Fragment key={`chat-key-${index}`}>
              <Card
                onClick={() => handleChatClick(chat)}
                sx={{
                  margin: "5px",
                  minHeight: 100,
                  cursor: "pointer",
                  backgroundColor: selectedChat?.id === chat?.id ? "background.lightBlue" : "transparent",
                  '&:hover': {
                    backgroundColor: selectedChat?.id !== chat?.id ? 'grey.main' : 'background.lightBlue',
                  },
                }}
              >
                <Stack direction={"row"} gap={2}>
                  {!hasPermission(CAN_VIEW_ALL_CHATS) ? (
                    <GenericAvatar size={"46x"} location={details?.details?.profile_picture?.location} />
                  ) : (
                    <Box>
                      <Box sx={{ position: "relative", width: "50px", height: "46px" }}>
                        <Box sx={{ position: "absolute", left: 0, top: -5, zIndex: 2 }}>
                          <GenericAvatar size="32px" location={chat?.requested_by?.details?.profile_picture?.location} />
                        </Box>
                        <Box sx={{ position: "absolute", left: 20, bottom: 5, zIndex: 1 }}>
                          <GenericAvatar size="32px" location={chat?.requested_to?.details?.profile_picture?.location} />
                        </Box>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ width: "100%" }}>
                    <Grid container justifyContent={"space-between"}>
                      <Grid item>
                        <Typography variant="subtitle1" fontWeight={600} noWrap width={180}>
                          {details?.name}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ color: !isUnread ? "gray" : "primary.main" }}
                        >
                          <Typography variant="caption" fontWeight={isUnread ? 700 : 400}>
                            {latestMessage?.created_at
                              ? formatDistanceToNow(new Date(latestMessage?.created_at ?? 0), { addSuffix: true })
                              : formatDistanceToNow(new Date(chat?.created_at ?? 0), { addSuffix: true })}
                          </Typography>
                          {isUnread && <Circle size={8} weight="fill" />}
                        </Stack>
                      </Grid>
                    </Grid>
                    <Typography variant="body2" color="gray" fontWeight={isUnread ? 700 : 400} width={350} noWrap>
                      {latestMessage?.message ?? "You can now start chatting."}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
              {index !== chatListModel?.data?.length - 1 && <GenericDivider margin="0px" />}
            </Fragment>
          );
        })}
        {isLoadingChatList && (<Typography textAlign="center" py={2}>  Loading...</Typography>)}
      </Box>
    </Box>
  );
};
